(ns ui.view.transaction-process.create-modal
  (:require
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [common.transaction-process.access :as tp-access]
   [common.transaction-process.config :as config]
   [common.transaction-process.templates.registry :as template-registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [ui.completion.new-entity :as new-entity]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn- get-vehicle-config
  [vehicle-id]
  (when vehicle-id
    (let [fund (cu/find-first-by :db/id vehicle-id @session/funds)]
      (config/parse-vehicle-config (:fund/transaction-process-config fund)))))

(defn- user-can-use-template?
  [{:keys [ctx fund-entity template-kw]}]
  (and (template-registry/get-template template-kw)
       (tp-access/can-create-transaction-process?
        (assoc ctx
               :vehicle fund-entity
               :template-kw template-kw))))

(defn fund-options
  []
  (let [ctx {:tenant                      @session/tenant
             :tenant-plan                 @session/tenant-plan
             :role                        @session/role
             :account                     @session/account
             :account-current-permissions @session/current-permissions}]
    (->> @session/funds
         (filter (fn [f]
                   (let [vc (config/parse-vehicle-config (:fund/transaction-process-config f))]
                     (->> (config/templates-for-vehicle vc)
                          (some (fn [kw]
                                  (user-can-use-template? {:ctx         ctx
                                                           :fund-entity f
                                                           :template-kw kw})))))))
         (map (fn [f]
                {:value (:db/id f)
                 :label (entity/display-string f)}))
         (sort-by :label)
         (vec))))

(defn- template-options
  [vehicle-id]
  (when-let [vc (get-vehicle-config vehicle-id)]
    (let [ctx         {:tenant                      @session/tenant
                       :tenant-plan                 @session/tenant-plan
                       :role                        @session/role
                       :account                     @session/account
                       :account-current-permissions @session/current-permissions}
          fund-entity (cu/find-first-by :db/id vehicle-id @session/funds)]
      (->> (config/templates-for-vehicle vc)
           (keep (fn [kw]
                   (when (user-can-use-template? {:ctx         ctx
                                                  :fund-entity fund-entity
                                                  :template-kw kw})
                     (when-let [t (template-registry/get-template kw)]
                       {:value kw
                        :label (:template/name t)}))))
           vec))))

(defn- auto-selected-template-kw
  [vehicle-id]
  (let [templates (template-options vehicle-id)]
    (when (= 1 (count templates))
      (:value (first templates)))))

(defn create-entity-data
  [{:keys [short-name vehicle-id template-kw transaction-type target-id]}]
  (cu/remove-nil-vals
   {:entity/type                          :entity.type/transaction-process
    :transaction-process/short-name       (cu/str-trim short-name)
    :transaction-process/vehicle          vehicle-id
    :transaction-process/target           target-id
    :transaction-process/transaction-type transaction-type
    :transaction-process/template-kw      template-kw
    :transaction-process/status           :transaction-process.status/active}))

(defn open-created-process!
  "Navigates to the process that was just created, unless the caller supplied
   `on-created`.

   Routes by explicit type: a new-grid account never has a transaction process
   in `@session/entities`, so `routing/open-entity` given only an id resolves no
   type and silently does nothing."
  [{:keys [new-id on-created]}]
  (if on-created
    (on-created new-id)
    (routing/open-entity-type :entity.type/transaction-process new-id)))

(defn view
  [{:keys [on-close initial-values on-created]}]
  (let [initial-state (merge {:short-name       ""
                              :vehicle-id       nil
                              :template-kw      nil
                              :transaction-type nil
                              :target-id        nil}
                             initial-values)
        initial-state (if (and (:vehicle-id initial-state)
                               (nil? (:template-kw initial-state)))
                        (assoc initial-state
                               :template-kw
                               (auto-selected-template-kw (:vehicle-id initial-state)))
                        initial-state)
        form-state (r/atom initial-state)
        submitting? (r/atom false)
        error (r/atom nil)]
    (fn [_props]
      (let [{:keys [short-name vehicle-id template-kw transaction-type]} @form-state
            trimmed-short-name (cu/str-trim short-name)
            templates (when vehicle-id (template-options vehicle-id))
            no-templates? (and vehicle-id (empty? templates))
            single-template? (= 1 (count templates))
            selected-template (when template-kw (template-registry/get-template template-kw))
            transaction-type-options (when selected-template
                                       (->> (:template/transaction-types selected-template)
                                            (mapv (fn [{:transaction-type/keys [id label]}]
                                                    {:value id :label label}))))
            transaction-type-ok? (or (empty? transaction-type-options)
                                     (some? transaction-type))
            can-submit? (and (cu/non-empty-string? trimmed-short-name)
                             vehicle-id
                             template-kw
                             transaction-type-ok?
                             (not @submitting?))]
        [modal/view
         {:visible? true
          :hide!    on-close}
         (tr :transaction-process/create)
         [:div {:class "flex flex-col gap-4"}
          [labelled-input/view
           {:label     (tr :name)
            :data-type :data.type/text-simple
            :value     short-name
            :on-change (fn [v] (swap! form-state assoc :short-name v))}]
          [labelled-input/view
           {:label     (tr :vehicle)
            :data-type :data.type/keyword
            :value     vehicle-id
            :options   (fund-options)
            :on-change (fn [v]
                         (swap! form-state assoc
                                :vehicle-id v
                                :template-kw (auto-selected-template-kw v)
                                :transaction-type nil))}]
          (when (and vehicle-id (not no-templates?))
            [labelled-input/view
             {:label     (tr :template)
              :data-type :data.type/keyword
              :value     template-kw
              :options   templates
              :disabled? single-template?
              :on-change (fn [v] (swap! form-state assoc :template-kw v :transaction-type nil))}])
          (when no-templates?
            [:div {:class "text-sm m-0 p-3 border border-yellow-200 bg-yellow-50 rounded text-yellow-800"}
             (tr :transaction-process/no-templates-for-vehicle)])
          (when (seq transaction-type-options)
            [labelled-input/view
             {:label     (tr :transaction-process/transaction-type)
              :data-type :data.type/keyword
              :value     transaction-type
              :options   transaction-type-options
              :disabled? (not template-kw)
              :on-change (fn [v] (swap! form-state assoc :transaction-type v))}])
          (when @error
            [:div {:class "text-red-600 text-sm m-0"} @error])]
         [modal/footer-children-left-and-right
          [[button/secondary-hollow
            {:on-click on-close}
            (tr :cancel)]]
          [[button/primary-solid
            {:disabled? (not can-submit?)
             :on-click  (fn []
                          (reset! submitting? true)
                          (reset! error nil)
                          (entity-actions/*->create
                           (create-entity-data @form-state)
                           (fn [resp]
                             (reset! submitting? false)
                             (on-close)
                             (let [new-id (-> resp :body :db/id)]
                               (new-entity/wait-for-searchable!
                                {:new-entity-id new-id
                                 ;; carries the type, so a transaction process -
                                 ;; not a bulk-synced type - waits on the search
                                 ;; index rather than on session sync, which
                                 ;; would never complete for it
                                 :new-entity    (:body resp)
                                 ;; sibling of :body in the envelope - the handle
                                 ;; for the off-thread indexing this create
                                 ;; started
                                 :completion-id (:completion/id resp)
                                 :close-cb!     (fn []
                                                  (open-created-process!
                                                   {:new-id     new-id
                                                    :on-created on-created}))})))
                           (fn [err]
                             (reset! submitting? false)
                             (reset! error (tr :transaction-process/create-error))
                             (logging/error "Failed to create transaction process" err))))}
            (if @submitting? (tr :creating) (tr :transaction-process/create))]]]]))))
